import { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, Package,
  Calendar, Download, Loader2, ChevronLeft,  ChevronRight,
  AlertCircle, BarChart3, FileSpreadsheet,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { reportsService, CostReportItem, CostReportSummary } from '@/services/reports.service';
import { ProfitChart } from '@/components/ProfitChart';
import * as XLSX from 'xlsx';

function formatCurrency(n: number) {
  return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPercent(n: number) {
  return (Number(n) || 0).toFixed(1) + '%';
}

function getDefaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

const groupByLabels: Record<string, string> = {
  product: 'Producto',
  day: 'Día',
  month: 'Mes',
};

export function ReportsPage() {
  const { user } = useAuthStore();
  const range = getDefaultRange();
  const [startDate, setStartDate] = useState(range.start);
  const [endDate, setEndDate] = useState(range.end);
  const [groupBy, setGroupBy] = useState<string>('product');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CostReportItem[]>([]);
  const [summary, setSummary] = useState<CostReportSummary | null>(null);

  const load = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await reportsService.getCosts({
        companyId: user.company.id,
        startDate,
        endDate,
        groupBy: groupBy as 'product' | 'day' | 'month',
      });
      setData(result.data);
      setSummary(result.summary);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Error al cargar el reporte';
      setError(msg);
      setData([]);
      setSummary(null);
    }
    setLoading(false);
  }, [user, startDate, endDate, groupBy]);

  useEffect(() => { load(); }, [load]);

  const handleExportExcel = () => {
    const headers = ['Producto', 'SKU', 'Categoría', 'Cantidad', 'Ventas', 'Costo', 'Utilidad', 'Margen %'];
    const rows = data.map(item => ({
      'Producto': item.label,
      'SKU': item.sku || '',
      'Categoría': item.category || '',
      'Cantidad': item.quantity,
      'Ventas': item.revenue,
      'Costo': item.cost,
      'Utilidad': item.profit,
      'Margen %': item.margin,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });

    // Column widths
    ws['!cols'] = headers.map((h: string) => {
      if (h === 'Producto') return { wch: 30 };
      if (h === 'SKU') return { wch: 15 };
      if (h === 'Categoría') return { wch: 20 };
      return { wch: 14 };
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    XLSX.writeFile(wb, `reporte-costos_${startDate}_${endDate}.xlsx`);
  };

  const MetricCard = ({ title, value, icon: Icon, color, subtitle }: {
    title: string; value: string; icon: any; color: string; subtitle?: string;
  }) => (
    <div className="bg-white rounded-[16px] p-4 border border-gray-100 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-[12px] flex items-center justify-center ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 font-medium">{title}</p>
          <p className="text-lg font-bold mt-0.5">{value}</p>
          {subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Reporte de Costos</h1>
          <p className="text-sm text-gray-500 mt-1">Utilidad bruta, margen y rendimiento por período</p>
        </div>
        <button
          onClick={handleExportExcel}
          disabled={data.length === 0}
          className="ios-button-secondary flex items-center gap-2 h-9 px-4 text-sm disabled:opacity-50"
        >
          <FileSpreadsheet size={16} />
          Exportar Excel
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-[16px] p-4 border border-gray-100 shadow-sm mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="ios-input h-9 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="ios-input h-9 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Agrupar por</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="ios-input h-9 text-sm"
            >
              <option value="product">Producto</option>
              <option value="day">Día</option>
              <option value="month">Mes</option>
            </select>
          </div>
          {summary && (
            <div className="flex items-center gap-2 text-xs text-gray-400 ml-auto">
              <Calendar size={14} />
              {summary.totalSales} ventas · {summary.totalItems} líneas · {summary.totalQuantity} unidades
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <MetricCard
            title="Ventas Totales"
            value={formatCurrency(summary.totalRevenue)}
            icon={TrendingUp}
            color="bg-blue-500"
          />
          <MetricCard
            title="Costo Total"
            value={formatCurrency(summary.totalCost)}
            icon={Package}
            color="bg-orange-500"
          />
          <MetricCard
            title="Utilidad Bruta"
            value={formatCurrency(summary.totalProfit)}
            icon={TrendingUp}
            color={summary.totalProfit >= 0 ? 'bg-green-500' : 'bg-red-500'}
            subtitle={summary.totalProfit >= 0 ? 'Ganancia' : 'Pérdida'}
          />
          <MetricCard
            title="Margen Promedio"
            value={formatPercent(summary.avgMargin)}
            icon={TrendingDown}
            color={summary.avgMargin >= 20 ? 'bg-green-500' : summary.avgMargin >= 10 ? 'bg-yellow-500' : 'bg-red-500'}
          />
        </div>
      )}

      {/* Chart */}
      {!loading && data.length > 0 && (
        <div className="bg-white rounded-[16px] border border-gray-100 shadow-sm p-5 mb-6">
          <ProfitChart data={data} groupBy={groupBy} />
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-[16px] border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="text-center py-16 text-red-400">
            <AlertCircle size={40} className="mx-auto mb-3 opacity-70" />
            <p className="font-medium text-red-500">Error al cargar el reporte</p>
            <p className="text-sm mt-1 text-red-400">{error}</p>
            <button onClick={load} className="mt-4 ios-button-primary h-9 px-4 text-sm">
              Reintentar
            </button>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Package size={40} className="mx-auto mb-3 opacity-50" />
            <p className="font-medium">Sin datos</p>
            <p className="text-sm mt-1">No hay ventas en el período seleccionado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">
                    {groupByLabels[groupBy] || 'Producto'}
                  </th>
                  {groupBy === 'product' && (
                    <>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">SKU</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Categoría</th>
                    </>
                  )}
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Cant.</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Ventas</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Costo</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Utilidad</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Margen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-sm">{item.label || 'Sin nombre'}</td>
                    {groupBy === 'product' && (
                      <>
                        <td className="px-4 py-3 text-gray-400 text-xs">{item.sku || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {item.category || 'Sin categoría'}
                          </span>
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-right">{item.quantity}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(item.revenue)}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{formatCurrency(item.cost)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${item.profit >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {formatCurrency(item.profit)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        item.margin >= 20 ? 'bg-green-50 text-green-700' :
                        item.margin >= 10 ? 'bg-yellow-50 text-yellow-700' :
                        item.margin >= 0 ? 'bg-red-50 text-red-600' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {formatPercent(item.margin)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
